import { Helmet } from "react-helmet-async";

// Re-export all structured data generators from the centralized module
export {
  organizationSchema,
  websiteSchema,
  servicesSchema,
  professionalServiceSchema,
  generateArticleSchema,
  generateCaseStudySchema,
  generateVideoSchema,
  generateFAQSchema,
  generateBreadcrumbSchema,
  generateSpeakableSchema,
  generateServicePageSchema,
  generateItemListSchema,
  combineSchemas,
  breadcrumbPaths,
  type FAQItem,
  type BreadcrumbItem,
} from "@/lib/structured-data";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  article?: {
    publishedTime?: string;
    author?: string;
  };
  /** Single structured data object or array of multiple schemas */
  structuredData?: object | object[];
  /** Breadcrumb items for BreadcrumbList schema */
  breadcrumbs?: Array<{ name: string; url: string }>;
  /** Prevent search engines from indexing this page */
  noindex?: boolean;
}

const SITE_NAME = "The Zoom Agency";
const SITE_URL = "https://www.thezoomagency.com";

const DEFAULT_OG_IMAGE = "/og-image.png";

export function SEO({
  title,
  description,
  keywords,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  article,
  structuredData,
  breadcrumbs,
  noindex,
}: SEOProps) {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = canonical ? (canonical.startsWith("http") ? canonical : `${SITE_URL}${canonical}`) : undefined;
  const ogImageUrl = ogImage.startsWith("http") ? ogImage : `${SITE_URL}${ogImage}`;

  // Generate breadcrumb schema if breadcrumbs provided
  const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`
    }))
  } : null;

  // Combine all structured data
  const allStructuredData: object[] = [];
  
  if (structuredData) {
    if (Array.isArray(structuredData)) {
      allStructuredData.push(...structuredData);
    } else {
      allStructuredData.push(structuredData);
    }
  }
  
  if (breadcrumbSchema) {
    allStructuredData.push(breadcrumbSchema);
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {/* keywords meta intentionally omitted — ignored by Google since 2009; signals weak SEO */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Canonical */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Hreflang for UK/US audiences */}
      {canonicalUrl && (
        <>
          <link rel="alternate" hrefLang="en-GB" href={canonicalUrl} />
          <link rel="alternate" hrefLang="en-US" href={canonicalUrl} />
          <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        </>
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={SITE_NAME} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={ogImageUrl} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {/* Article specific */}
      {article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article?.author && (
        <meta property="article:author" content={article.author} />
      )}

      {/* Structured Data - render each schema as its own script tag for maximum compatibility */}
      {allStructuredData.map((schema, index) => {
        // Ensure every schema has @context for Google to recognise it
        const withContext = (schema as Record<string, unknown>)["@context"]
          ? schema
          : { "@context": "https://schema.org", ...schema };
        return (
          <script key={index} type="application/ld+json">
            {JSON.stringify(withContext)}
          </script>
        );
      })}
    </Helmet>
  );
}
